/**
 * SpendingTrends.js — 6-month stacked bar chart for Need/Want/Savings spending
 *
 * Shows month-over-month spending patterns with interactive tap-to-detail.
 * Uses the same SVG + touch interaction pattern as CashFlowChart.
 *
 * Props:
 *   budgetHistory — array from getBudgetHistory(), sorted chronologically
 */
import React, { useState, useRef } from 'react';
import { formatCurrency, formatCompact } from '../utils/format';
import theme from '../utils/theme';

// Chart layout constants
const VIEW_W = 300;
const VIEW_H = 170;
const CHART_L = 40;
const CHART_R = 280;
const CHART_T = 10;
const CHART_B = 130;
const CHART_W = CHART_R - CHART_L;
const CHART_H = CHART_B - CHART_T;

// Bucket colors (match transaction dot colors from App.css)
const COLORS = {
  need: theme.bucketNeed,
  want: theme.bucketWant,
  savings: theme.bucketSavings,
};

/** Short month name from a "YYYY-MM" key */
function shortMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
}

export default function SpendingTrends({ budgetHistory }) {
  const [activeBar, setActiveBar] = useState(null);
  const svgRef = useRef(null);

  if (!budgetHistory || budgetHistory.length < 2) return null;

  const count = budgetHistory.length;

  // Extract bucket totals per month
  const bars = budgetHistory.map(bp => ({
    monthKey: bp.monthKey,
    need: Math.abs(bp.need_actual || 0),
    want: Math.abs(bp.want_actual || 0),
    savings: Math.abs(bp.savings_actual || 0),
    total: Math.abs(bp.need_actual || 0) + Math.abs(bp.want_actual || 0) + Math.abs(bp.savings_actual || 0),
  }));

  // Y scale
  const maxTotal = Math.max(...bars.map(b => b.total), 1);
  const yMax = maxTotal * 1.1; // 10% headroom

  // Y ticks (3 lines)
  const yTicks = [0, yMax / 2, yMax].map(v => ({
    value: v,
    y: CHART_T + (1 - v / yMax) * CHART_H,
    label: formatCompact(Math.round(v)),
  }));

  // Bar geometry
  const totalBarSpace = CHART_W / count;
  const barWidth = Math.min(totalBarSpace * 0.7, 35);
  const barOffset = (totalBarSpace - barWidth) / 2;

  // Handle click/tap → map to bar index
  const handleInteraction = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const svgX = ((clientX - rect.left) / rect.width) * VIEW_W;
    const barIndex = Math.floor((svgX - CHART_L) / totalBarSpace);
    const clamped = Math.max(0, Math.min(barIndex, count - 1));
    setActiveBar(prev => prev === clamped ? null : clamped);
  };

  const activeData = activeBar != null ? bars[activeBar] : null;

  // Compute delta vs prior month
  let delta = null;
  if (activeBar != null && activeBar > 0) {
    const prev = bars[activeBar - 1];
    const curr = bars[activeBar];
    delta = {
      total: curr.total - prev.total,
      need: curr.need - prev.need,
      want: curr.want - prev.want,
      savings: curr.savings - prev.savings,
    };
  }

  return (
    <div className="card trends-card">
      <h3>Spending Trends</h3>

      <div className="trends-chart-wrap">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          onClick={handleInteraction}
          onTouchStart={handleInteraction}
        >
          {/* Grid lines */}
          {yTicks.map((t, i) => (
            <line
              key={i}
              x1={CHART_L} y1={t.y} x2={CHART_R} y2={t.y}
              stroke={theme.border} strokeWidth="0.5" strokeDasharray="3,2"
            />
          ))}

          {/* Stacked bars */}
          {bars.map((bar, i) => {
            const x = CHART_L + i * totalBarSpace + barOffset;
            const isActive = activeBar === i;

            // Stack: Need on bottom, Want in middle, Savings on top
            const needH = (bar.need / yMax) * CHART_H;
            const wantH = (bar.want / yMax) * CHART_H;
            const savingsH = (bar.savings / yMax) * CHART_H;

            const needY = CHART_B - needH;
            const wantY = needY - wantH;
            const savingsY = wantY - savingsH;

            return (
              <g key={i}>
                {/* Need (bottom) */}
                {bar.need > 0 && (
                  <rect x={x} y={needY} width={barWidth} height={needH}
                    fill={COLORS.need} rx="2"
                    opacity={isActive ? 1 : 0.8}
                  />
                )}
                {/* Want (middle) */}
                {bar.want > 0 && (
                  <rect x={x} y={wantY} width={barWidth} height={wantH}
                    fill={COLORS.want} rx="0"
                    opacity={isActive ? 1 : 0.8}
                  />
                )}
                {/* Savings (top) */}
                {bar.savings > 0 && (
                  <rect x={x} y={savingsY} width={barWidth} height={savingsH}
                    fill={COLORS.savings} rx="2"
                    opacity={isActive ? 1 : 0.8}
                  />
                )}
                {/* Active highlight */}
                {isActive && (
                  <rect x={x - 1} y={savingsY - 1}
                    width={barWidth + 2} height={CHART_B - savingsY + 2}
                    fill="none" stroke={theme.primary} strokeWidth="1" rx="3"
                  />
                )}
              </g>
            );
          })}

          {/* Y-axis labels */}
          {yTicks.map((t, i) => (
            <text key={i} x={CHART_L - 4} y={t.y + 3}
              className="trends-y-label" textAnchor="end"
            >
              {t.label}
            </text>
          ))}

          {/* X-axis labels */}
          {bars.map((bar, i) => (
            <text key={i}
              x={CHART_L + i * totalBarSpace + totalBarSpace / 2}
              y={CHART_B + 14}
              className="trends-x-label" textAnchor="middle"
            >
              {shortMonth(bar.monthKey)}
            </text>
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="trends-legend">
        <span className="cashflow-legend-item">
          <span className="trends-legend-dot need" /> Needs
        </span>
        <span className="cashflow-legend-item">
          <span className="trends-legend-dot want" /> Wants
        </span>
        <span className="cashflow-legend-item">
          <span className="trends-legend-dot savings" /> Savings
        </span>
      </div>

      {/* Detail panel */}
      {activeData && (
        <div className="trends-detail">
          <div className="trends-detail-row">
            <span className="trends-detail-month">{shortMonth(activeData.monthKey)} Total</span>
            <span className="trends-detail-total">{formatCurrency(activeData.total, false)}</span>
          </div>
          <div className="trends-detail-row">
            <span><span className="trends-legend-dot need" /> Needs</span>
            <span>
              {formatCurrency(activeData.need, false)}
              {delta && <DeltaBadge value={delta.need} />}
            </span>
          </div>
          <div className="trends-detail-row">
            <span><span className="trends-legend-dot want" /> Wants</span>
            <span>
              {formatCurrency(activeData.want, false)}
              {delta && <DeltaBadge value={delta.want} />}
            </span>
          </div>
          <div className="trends-detail-row">
            <span><span className="trends-legend-dot savings" /> Savings</span>
            <span>
              {formatCurrency(activeData.savings, false)}
              {delta && <DeltaBadge value={delta.savings} />}
            </span>
          </div>
          {delta && (
            <div className="trends-detail-row trends-detail-total-row">
              <span>vs. prior month</span>
              <DeltaBadge value={delta.total} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Small delta badge: "↑$200" (red) or "↓$150" (green) */
function DeltaBadge({ value }) {
  if (value === 0) return null;
  const up = value > 0;
  return (
    <span className={`trends-delta ${up ? 'up' : 'down'}`}>
      {up ? '↑' : '↓'}{formatCompact(Math.abs(value))}
    </span>
  );
}
